/**
 * Convex helpers - API references and small client-side utilities
 *
 * API references are string-based function references compatible with
 * Convex's useQuery, useMutation, ConvexReactClient, and ConvexHttpClient.
 * These are generated manually to avoid needing `npx convex dev` for codegen.
 */

// Convex function references - each value is "module:functionName"
export const api = {
  songs: {
    get: "songs:get",
    getByYoutubeId: "songs:getByYoutubeId",
    list: "songs:list",
    listIntegrityIssues: "songs:listIntegrityIssues",
    search: "songs:search",
    create: "songs:create",
    updateMetadata: "songs:updateMetadata",
    updateProcessingStatus: "songs:updateProcessingStatus",
    updateLyrics: "songs:updateLyrics",
    updateWordLevelTranscription: "songs:updateWordLevelTranscription",
    deleteSong: "songs:deleteSong",
  },
  rooms: {
    getByCode: "rooms:getByCode",
    listAll: "rooms:listAll",
    listAdmin: "rooms:listAll",
    create: "rooms:create",
    touchActivity: "rooms:updateActivity",
    getAdminQueue: "queue:getQueue",
  },
  queue: {
    getForRoom: "queue:getQueue",
    getQueue: "queue:getQueue",
    addToQueue: "queue:addToQueue",
    removeFromQueue: "queue:removeFromQueue",
    reorderQueue: "queue:reorderQueue",
  },
  playback: {
    getState: "playback:getState",
    play: "playback:play",
    pause: "playback:pause",
    seek: "playback:seek",
    nextSong: "playback:nextSong",
    previousSong: "playback:previousSong",
    songEnded: "playback:songEnded",
    adjustPitch: "playback:adjustPitch",
    updatePosition: "playback:updatePosition",
    setCursor: "playback:setCursor",
  },
  processingJobs: {
    list: "processingJobs:list",
    getByJobId: "processingJobs:getByJobId",
    getQueueStats: "processingJobs:getQueueStats",
    create: "processingJobs:create",
    updateProgress: "processingJobs:updateProgress",
    completeJob: "processingJobs:completeJob",
    claimNextPending: "processingJobs:claimNextPending",
    retryJob: "processingJobs:retryJob",
    deleteJob: "processingJobs:deleteJob",
    checkDuplicate: "processingJobs:checkDuplicate",
    ensureLyricsCleanup: "processingJobs:ensureLyricsCleanup",
  },
  failedIngestions: {
    list: "failedIngestions:list",
    getStats: "failedIngestions:getStats",
    create: "failedIngestions:create",
    acknowledge: "failedIngestions:acknowledge",
    remove: "failedIngestions:remove",
  },
  emoji: {
    send: "emoji:send",
    recent: "emoji:recent",
    cleanup: "emoji:cleanup",
  },
  analytics: {
    getWrappedStats: "analytics:getWrappedStats",
  },
}

/**
 * Convex documents use `_id`; UI code expects `id` for comparisons and keys.
 */
export function coerceSong(song) {
  if (!song) return null
  const id = song._id ?? song.id ?? null
  return { ...song, id }
}

/**
 * Extract YouTube video ID from URL
 */
export function extractYouTubeId(url) {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /youtube\.com\/watch\?.*v=([^&\n?#]+)/,
  ]
  for (const pattern of patterns) {
    const match = url.match(pattern)
    if (match) return match[1]
  }
  throw new Error('Invalid YouTube URL')
}

/**
 * Sanitize YouTube URL to clean video URL
 */
export function sanitizeYouTubeUrl(url) {
  try {
    const videoId = extractYouTubeId(url)
    return `https://www.youtube.com/watch?v=${videoId}`
  } catch {
    return url
  }
}
